// P1-016: Agent formation management panel — Neon Industrial design
import { useState, useMemo } from 'react';
import { AgentType } from '@/shared/types/common';
import { useAgentStore } from '../stores/agentStore';
import type { OwnedAgent } from '../stores/agentStore';
import { useHardwareStore } from '../stores/hardwareStore';
import { AgentDeployer } from '../systems/AgentDeployer';
import { Button } from '@/shared/ui/Button';
import { AgentSlot } from './AgentSlot';
import { typeBgColor, typeAbbr, typeLabelCN } from '../utils/agent-ui-helpers';

/** Agent type filter options */
const TYPE_FILTERS: { type: AgentType | 'ALL'; label: string }[] = [
  { type: 'ALL',                label: '全部' },
  { type: AgentType.BACKEND,    label: '后端' },
  { type: AgentType.FRONTEND,   label: '前端' },
  { type: AgentType.TESTING,    label: '测试' },
  { type: AgentType.DEVOPS,     label: '运维' },
  { type: AgentType.HACKER,     label: '黑客' },
];

interface AgentTerminalProps {
  onConfirm?: () => void;
}

export function AgentTerminal({ onConfirm }: AgentTerminalProps) {
  const [typeFilter, setTypeFilter] = useState<AgentType | 'ALL'>('ALL');
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

  const ownedAgents = useAgentStore((s) => s.ownedAgents);
  const formation = useAgentStore((s) => s.formation);
  const setFormationSlot = useAgentStore((s) => s.setFormationSlot);
  const removeFromFormation = useAgentStore((s) => s.removeFromFormation);
  const totalCompute = useHardwareStore((s) => s.getTotalCompute());

  // Filter agents
  const filteredAgents = useMemo(() => {
    let agents = [...ownedAgents];
    if (typeFilter !== 'ALL') {
      agents = agents.filter((a) => a.type === typeFilter);
    }
    agents.sort((a, b) => {
      const aDeployed = formation.some((f) => f?.instanceId === a.instanceId) ? 0 : 1;
      const bDeployed = formation.some((f) => f?.instanceId === b.instanceId) ? 0 : 1;
      if (aDeployed !== bDeployed) return aDeployed - bDeployed;
      if (a.rarity !== b.rarity) return b.rarity - a.rarity;
      return b.level - a.level;
    });
    return agents;
  }, [ownedAgents, typeFilter, formation]);

  const teamComputeCost = AgentDeployer.calculateTeamComputeCost(formation);
  const canDeploy = teamComputeCost <= totalCompute;
  const deployedCount = formation.filter((a) => a !== null).length;

  const handleAgentClick = (agent: OwnedAgent) => {
    setSelectedAgent(agent.instanceId);
  };

  const handleSlotClick = (slotIndex: number) => {
    if (!selectedAgent) return;
    const isDeployed = formation.some((a) => a?.instanceId === selectedAgent);
    if (isDeployed) return;
    setFormationSlot(slotIndex, selectedAgent);
  };

  const handleRemove = (slotIndex: number) => {
    removeFromFormation(slotIndex);
  };

  const confirmDisabled = deployedCount !== 5 || !canDeploy;

  return (
    <div className="agent-terminal">
      {/* Title */}
      <h2 className="agent-terminal__title">卡牌 AGENT TERMINAL</h2>

      {/* Header row: filters + formation label */}
      <div className="agent-terminal__header">
        <div className="agent-terminal__filters">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.type}
              className={`agent-terminal__filter-btn ${typeFilter === f.type ? 'agent-terminal__filter-btn--active' : ''}`}
              onClick={() => setTypeFilter(f.type)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="agent-terminal__formation-label">DEPLOY FORMATION</span>
      </div>

      {/* 3-column layout */}
      <div className="agent-terminal__top">
        {/* Left: Agent list */}
        <div className="agent-terminal__list">
          <div className="agent-terminal__agent-list">
            {filteredAgents.map((agent) => {
              const isDeployed = formation.some((f) => f?.instanceId === agent.instanceId);
              return (
                <div
                  key={agent.instanceId}
                  className={`agent-terminal__agent-item ${
                    selectedAgent === agent.instanceId ? 'agent-terminal__agent-item--selected' : ''
                  } ${isDeployed ? 'agent-terminal__agent-item--deployed' : ''}`}
                  style={{ borderLeftColor: typeBgColor(agent.type) }}
                  onClick={() => handleAgentClick(agent)}
                >
                  <div
                    className="agent-terminal__type-badge"
                    style={{ background: typeBgColor(agent.type) }}
                  >
                    {typeAbbr(agent.type)}
                  </div>
                  <div className="agent-terminal__agent-info">
                    <div className="agent-terminal__agent-name">
                      {agent.name}
                    </div>
                    <div className="agent-terminal__agent-meta">
                      {typeLabelCN(agent.type)} · LV.{agent.level} · {'★'.repeat(agent.rarity)}
                      {isDeployed && ' [已部署]'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Center: 5 formation slots */}
        <div className="agent-terminal__formation">
          <div className="agent-terminal__slots">
            {formation.map((agent, idx) => (
              <AgentSlot
                key={idx}
                agent={agent}
                index={idx}
                onClickEmpty={handleSlotClick}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>

      </div>

      {/* Bottom: Compute status + confirm */}
      <div className="agent-terminal__bottom">
        <div className="agent-terminal__compute-status">
          算力:{' '}
          <span className={canDeploy ? 'agent-terminal__compute-ok' : 'agent-terminal__compute-overload'}>
            {teamComputeCost}/{totalCompute}
          </span>
          {' '}
          {canDeploy
            ? <span className="agent-terminal__compute-ok">[OK]</span>
            : <span className="agent-terminal__compute-overload">[OVERLOAD!]</span>
          }
          {deployedCount < 5 && (
            <span style={{ color: 'var(--color-warning)', marginLeft: 'var(--space-sm)' }}>
              编队: {deployedCount}/5
            </span>
          )}
        </div>
        <Button
          variant="primary"
          disabled={confirmDisabled}
          onClick={onConfirm}
        >
          确认部署
        </Button>
      </div>
    </div>
  );
}
